import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { useLazyQuery } from "@apollo/client";

import { components } from "../../../components";
import AuthLayout from "../../../services/auth/jwt/withAuth";
import TradeHeader from "../../../components/shared/TradeHeader";
import MyTradeTabButton from "../../../components/shared/MyTradeTabButton";
import { TRADE_STATUS } from "../../../constants/constants";
import {
  useQueryCountMyTrades,
  useQueryMyTrades,
} from "../../../graphql/apollo/useQuery";
import { Trade } from "../../../types/models/Trade";
import MyTradeListItem from "./components/MyTradeListItem";
import { localeDate } from "../../../utils/dateUtils";
import { colors } from "../../../constants/colors";
import AppDialog from "../../../components/apps/AppDialog";
import { theme } from "../../../constants";
import MyTradeDetail from "./components/MyTradeDetail";
import { AppState, dispatch, useSelector } from "../../../store/store";
import { myTradeActions } from "../../../store/slices/mytradeSlice";
import { GET_MY_TRADE_DETAIL } from "../../../graphql/queries/trade";

export const MyTrades: React.FC = () => {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState<string>(TRADE_STATUS.ACTIVE);
  const { myTradeList, refetch } = useQueryMyTrades(tab);
  const { refetch: refetchCount } = useQueryCountMyTrades();
  const myTrades: Trade[] = myTradeList || [];
  const groupedResult = _.groupBy(myTrades, (trade) => {
    const date = new Date(trade.closingTime);
    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
  });

  const { detailDialog } = useSelector<AppState["mytrade"]>(
    ({ mytrade }) => mytrade
  );
  const [fetchTradeDetail, { data }] = useLazyQuery(GET_MY_TRADE_DETAIL);
  const tradeDetail: Trade = data?.myTradeDetail ?? {};

  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
    };

    scrollToTop();
  }, [pathname]);

  const renderBottomTabBar = (): JSX.Element => {
    return <components.BottomTabBar />;
  };

  const handleTab = (status: string) => {
    setTab(status);
    refetch();
  };

  const toggledialog = () => dispatch(myTradeActions.toggleDetailDialog());

  const handleClickDetail = (item: Trade) => {
    fetchTradeDetail({ variables: { id: item.id } });
    dispatch(myTradeActions.setMyTradeDetail(item));
    dispatch(myTradeActions.toggleDetailDialog());
  };

  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
      refetchCount();
    }, 30000); // 30 seconds

    return () => clearInterval(interval); // Cleanup interval on component unmount
  }, [refetch, refetchCount]);

  return (
    <AuthLayout>
      <AppDialog
        tradeDetail={tradeDetail}
        open={detailDialog && tradeDetail.id !== undefined}
        onClose={toggledialog}
        fullScreen
        backgroundColor={theme.colors.backgroundColorDark}
        leftHeader
      >
        {tradeDetail && (
          <MyTradeDetail
            item={tradeDetail}
            active={tab === TRADE_STATUS.ACTIVE}
          />
        )}
      </AppDialog>
      <main className="my-trades">
        <TradeHeader />
        <div className="my-trades__content">
          <Typography variant="h6">{t("myTrades")}</Typography>
          <div className="my-trades__content-tabs">
            <MyTradeTabButton
              onClick={() => handleTab(TRADE_STATUS.ACTIVE)}
              text={t("active")}
              isActive={tab === TRADE_STATUS.ACTIVE}
            />
            <MyTradeTabButton
              onClick={() => handleTab(TRADE_STATUS.CLOSED)}
              text={t("closed")}
              isActive={tab === TRADE_STATUS.CLOSED}
            />
          </div>
          <div className="my-trades__content-list">
            {Object.entries(groupedResult).map(([date, trades]) => {
              const dates = new Date(trades[0].closingTime);

              return (
                <div key={date}>
                  <Typography
                    variant="body2"
                    color={colors.darkGrey}
                    sx={{ ml: 1 }}
                  >
                    {localeDate(dates, i18n.language)}
                  </Typography>
                  <div>
                    {trades?.map((trade: Trade) => (
                      <div key={trade.id}>
                        <MyTradeListItem
                          onClick={() => handleClickDetail(trade)}
                          item={trade}
                          active={tab === TRADE_STATUS.ACTIVE}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>
      {renderBottomTabBar()}
    </AuthLayout>
  );
};
